import { useCallback, useEffect, useRef, useState } from 'react';
import { useFilteredOptionsLazyQuery, type UnitSystemEnum } from '@/__generated__/graphql';
import { formatTemplateFieldValue } from '@/utils';
import type { AssemblyTemplateQuery } from '@/types';
import type { FilterField, OptionItem } from './InsightsSearchBar.types';
import { MAX_VISIBLE_ITEMS, SEARCH_DEBOUNCE_MS } from './constants';
import { filterOptionsBySearchTerm } from './utils';

interface UseServerOptionsParams {
  activeFieldId: string | null;
  filterFields: FilterField[];
  assemblyTemplate: AssemblyTemplateQuery | undefined;
  unitSystem: UnitSystemEnum;
  structurePipes: Record<string, number> | null | undefined;
}

export function useServerOptions({
  activeFieldId,
  filterFields,
  assemblyTemplate,
  unitSystem,
  structurePipes,
}: UseServerOptionsParams) {
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [serverOptions, setServerOptions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const latestRequestRef = useRef<string | null>(null);

  const [fetchFilteredOptions, { loading: queryLoading }] = useFilteredOptionsLazyQuery({
    fetchPolicy: 'cache-first',
  });

  // Debounce input value
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(inputValue);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [inputValue]);

  // Fetch options when active field or search changes
  useEffect(() => {
    if (!activeFieldId) return;

    const field = filterFields.find((f) => f.id === activeFieldId);
    if (!field || field.type !== 'template' || !field.templateField) return;

    const templateId = assemblyTemplate?.id;
    const templateField = field.templateField;
    const fieldKey = templateField?.key ?? templateField?.id ?? '';

    if (!templateId || !fieldKey) {
      setIsLoading(false);
      return;
    }

    const requestKey = `${templateId}:${fieldKey}:${debouncedSearch}`;
    latestRequestRef.current = requestKey;
    setIsLoading(true);
    setServerOptions([]);

    fetchFilteredOptions({
      variables: {
        templateId,
        fieldKey,
        fieldId: templateField?.id,
        count: MAX_VISIBLE_ITEMS,
        ...(debouncedSearch && { q: debouncedSearch }),
      },
    })
      .then((result) => {
        if (latestRequestRef.current !== requestKey) return;
        setServerOptions(result.data?.filteredOptions?.[0]?.options ?? []);
        setIsLoading(false);
      })
      .catch(() => {
        if (latestRequestRef.current !== requestKey) return;
        setServerOptions([]);
        setIsLoading(false);
      });
  }, [activeFieldId, filterFields, debouncedSearch, assemblyTemplate?.id, fetchFilteredOptions]);

  const getDisplayOptions = useCallback(
    (activeField: FilterField | undefined): OptionItem[] => {
      if (!activeField) return [];

      if (activeField.type === 'template') {
        const templateField = activeField.templateField;
        const fieldKey = templateField?.key ?? templateField?.id ?? '';
        const templateId = assemblyTemplate?.id;

        if (!templateId || !fieldKey) {
          return filterOptionsBySearchTerm(activeField.options ?? [], inputValue);
        }

        return serverOptions.map((option) => ({
          id: option,
          display: formatTemplateFieldValue({
            value: option,
            templateFieldKey: fieldKey,
            unit: templateField?.unit,
            displayUnit: templateField?.displayUnit,
            unitSystem,
            structurePipes,
          }),
        }));
      }

      return filterOptionsBySearchTerm(activeField.options ?? [], inputValue);
    },
    [serverOptions, inputValue, assemblyTemplate?.id, unitSystem, structurePipes]
  );

  const isFieldLoading =
    activeFieldId &&
    filterFields.find((f) => f.id === activeFieldId)?.type === 'template' &&
    assemblyTemplate?.id &&
    (queryLoading || isLoading);

  return {
    inputValue,
    setInputValue,
    getDisplayOptions,
    isFieldLoading: Boolean(isFieldLoading),
  };
}
